import { StandardEvents } from '@shopify/events';
import { trackEvent } from '@theme/constructor-client';
import {
  itemDetailLoaded,
  addToCartConversions,
  autocompleteSelected,
  searchSubmitted,
  searchFocused,
} from '@theme/constructor-tracker';

/** Product pages already reported, so a variant change or a re-render does not count twice. */
const viewedProducts = new Set();

document.addEventListener(StandardEvents.productView, (event) => {
  const view = itemDetailLoaded(/** @type {any} */ (event), location.href);
  const itemId = /** @type {any} */ (view?.args[0])?.itemId;
  if (!view || viewedProducts.has(itemId)) return;

  viewedProducts.add(itemId);
  trackEvent(view);
});

document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
  const update = /** @type {any} */ (event);
  // Only the settled add knows whether it worked and what the line cost.
  update.promise
    ?.then((/** @type {any} */ result) => addToCartConversions(update, result).forEach(trackEvent))
    .catch(() => {});
});

/** @param {EventTarget | null} target */
const searchFormOf = (target) =>
  target instanceof HTMLFormElement && new URL(target.action, location.origin).pathname.endsWith('/search')
    ? target
    : null;

/**
 * Set while an Enter press is being handled. With results showing, the predictive search turns
 * Enter into a navigation and no `submit` fires; without them the form submits natively. Both
 * paths start at the key, so the key reports the search and a submit in the same task is skipped.
 */
let submittedByKey = false;

document.addEventListener(
  'keydown',
  (event) => {
    const input = event.target;
    if (event.key !== 'Enter' || event.isComposing || !(input instanceof HTMLInputElement)) return;

    const form = input.name === 'q' ? searchFormOf(input.form) : null;
    if (!form || form.querySelector('[aria-selected="true"]')) return;

    trackEvent(searchSubmitted(input.value));
    submittedByKey = true;
    setTimeout(() => (submittedByKey = false));
  },
  true
);

document.addEventListener(
  'submit',
  (event) => {
    const form = searchFormOf(event.target);
    const term = form ? new FormData(form).get('q') : null;
    if (typeof term === 'string' && !submittedByKey) trackEvent(searchSubmitted(term));
  },
  true
);

document.addEventListener(
  'click',
  (event) => {
    const target = event.target;
    const option =
      target instanceof Element ? target.closest('a[href]')?.closest('[data-cnstrc-autocomplete-section]') : null;
    const originalQuery = option?.closest('[data-cnstrc-original-query]')?.getAttribute('data-cnstrc-original-query');
    if (!option || !originalQuery) return;

    autocompleteSelected({
      term: option.getAttribute('data-cnstrc-autocomplete-term') ?? '',
      originalQuery,
      section: option.getAttribute('data-cnstrc-autocomplete-section') ?? '',
      itemId: option.getAttribute('data-cnstrc-item-id') ?? undefined,
    }).forEach(trackEvent);
  },
  true
);

document.addEventListener('focusin', (event) => {
  const input = event.target;
  if (input instanceof HTMLInputElement && input.name === 'q' && searchFormOf(input.form)) {
    trackEvent(searchFocused());
  }
});
